import type {PointFeatureDefinition, PointFeatureStatus,} from './reference-point/ReferencePointSolver';
import type {ReferenceCreationContext} from './ReferenceCreationContext';
import type {ReferenceDiagnosticSeverity} from './ReferenceFeatureTypes';


/**
 * 基准点历史实体 ID。
 */
export const DATUM_POINT_HISTORY_ENTITY_ID = 'datum-point-manager';

/**
 * 基准点历史三维向量。
 */
export type DatumPointHistoryVector3 = readonly [number, number, number];

/**
 * 基准点历史诊断记录。
 */
export interface DatumPointHistoryDiagnostic {
  /** 诊断码。 */
  code: string;
  /** 诊断消息。 */
  message: string;
  /** 严重级别。 */
  severity: ReferenceDiagnosticSeverity;
}

/**
 * 单个正式基准点历史记录。
 */
export interface DatumPointHistoryRecord {
  /** 基准点数值 ID。 */
  pointId: number;
  /** 基准点名称。 */
  name: string;
  /** 基准点创建上下文。 */
  creationContext: ReferenceCreationContext;
  /** 基准点世界坐标。 */
  position: DatumPointHistoryVector3;
  /** 是否可见。 */
  visible: boolean;
  /** 是否为默认原点。 */
  isOrigin: boolean;
  /** 用户手动创建时间戳（毫秒），用于设计树时间线排序。 */
  createdAt?: number;
}

/**
 * 用户手动创建基准点的设计树时间线登记项。
 *
 * 设计说明：
 * 1. 默认原点是系统内置对象，不进入该时间线；
 * 2. 数组顺序与创建登记顺序一致，设计树投影可据此与特征/草图按时间合并排序；
 * 3. 该结构只保存 plain data，不持有任何 Three.js 或场景对象引用。
 */
export interface UserCreatedDatumPointTimelineEntry {
  /** 基准点稳定业务 ID。 */
  pointId: number;
  /** 创建时间戳（毫秒）。 */
  createdAt: number;
}

/**
 * 基准点特征历史记录。
 *
 * 设计说明：
 * 1. 正式基准点对象只保存最终点位，不保存求解定义；
 * 2. 装配体零部件移动后仍需要通过 `PointFeatureDefinition` 重算；
 * 3. 因此历史快照必须同步保存控制器特征记录，避免 Undo 后被旧记录重算复活，Redo 后又丢失重算能力。
 */
export interface DatumPointFeatureHistoryRecord {
  /** 特征 ID。 */
  featureId: string;
  /** 正式对象创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 基准点可复算定义。 */
  definition: PointFeatureDefinition;
  /** 最近一次求解状态。 */
  status: PointFeatureStatus;
  /** 最近一次求解诊断。 */
  diagnostics: DatumPointHistoryDiagnostic[];
  /** 当前点集合缓存。 */
  points: DatumPointHistoryVector3[];
  /** 是否可见。 */
  visible: boolean;
  /** 已创建的正式基准点 ID 列表。 */
  datumPointIds: number[];
}

/**
 * 基准点管理器历史快照。
 *
 * 设计说明：
 * 1. 快照只保存可序列化主状态，不保存 Three.js 对象；
 * 2. 默认原点是系统内置对象，不进入模型历史；
 * 3. 用户创建的零件态和装配体态基准点都通过该快照恢复；
 * 4. 控制器特征记录与正式对象快照共同恢复，保证撤销重做后仍可继续装配体联动重算。
 */
export interface DatumPointHistorySnapshot {
  /** 管理器历史实体 ID。 */
  managerId: string;
  /** 用户创建的正式基准点记录。 */
  points: DatumPointHistoryRecord[];
  /** 用户创建的基准点特征记录。 */
  features: DatumPointFeatureHistoryRecord[];
}

